
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { WardrobeItem, SavedOutfit, OutfitLayer } from '../types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getFriendlyErrorMessage(
  error: unknown,
  context: string,
  lang: string,
  t: (key: string, vars?: { [key: string]: string | number }) => string
): string {
    let rawMessage = 'An unknown error occurred.';
    if (error instanceof Error) {
        rawMessage = error.message;
    } else if (typeof error === 'string') {
        rawMessage = error;
    } else if (error) {
        rawMessage = String(error);
    }

    const lowerRawMessage = rawMessage.toLowerCase();

    // Check for specific Gemini API error patterns first
    if (lowerRawMessage.includes("safety policies")) {
        const reasonMatch = rawMessage.match(/Reason: (\w+)/);
        const reason = reasonMatch ? reasonMatch[1] : 'unknown';
        return t('error_safety_block', { reason });
    }

    if (lowerRawMessage.includes("api key not valid")) {
        return t('error_apiKey_invalid');
    }
    
    if (lowerRawMessage.includes("permission_denied")) {
         return t('error_apiKey_permission');
    }
    
    // Detect rate limit errors even if not JSON
    if (lowerRawMessage.includes("429") || lowerRawMessage.includes("resource_exhausted")) {
        return t('error_rateLimit');
    }

    // Handle 404 / Missing Resource
    if (lowerRawMessage.includes("404") || lowerRawMessage.includes("failed to load") || lowerRawMessage.includes("missing")) {
        return t('error_outfitCorrupted'); // Reusing "This saved outfit is corrupted..." as it is semantically close to missing resources.
    }
    
    // Storage Quota
    if (lowerRawMessage.includes("quota") || lowerRawMessage.includes("storage_full")) {
        return t('error_storage_full_outfit');
    }

    // Generic generation failure check (prevents "Unhandled error" logs for known generic issues)
    if (lowerRawMessage.includes("failed to generate")) {
        return t('canvas_generation_failed');
    }

    // Attempt to parse JSON for more detailed errors
    try {
        const errorJson = JSON.parse(rawMessage);
        const errorDetails = errorJson?.error;
        if (errorDetails) {
            if (errorDetails.status === 'RESOURCE_EXHAUSTED' || errorDetails.code === 429) {
                return t('error_rateLimit');
            }
            if (errorDetails.message?.toLowerCase().includes("api key not valid")) {
                return t('error_apiKey_invalid');
            }
        }
    } catch (e) {
        // Not a JSON error message, proceed.
    }
    
    // Check for common network errors
    if (lowerRawMessage.includes("failed to fetch")) {
        return t('error_network');
    }

    if (rawMessage.includes("Unsupported MIME type")) {
        try {
            const errorJson = JSON.parse(rawMessage);
            const nestedMessage = errorJson?.error?.message;
            if (nestedMessage && nestedMessage.includes("Unsupported MIME type")) {
                const mimeType = nestedMessage.split(': ')[1] || 'unsupported';
                return t('error_unsupported_mime', { mimeType });
            }
        } catch (e) {
        }
        return t('error_unsupported_format');
    }
    
    // Fallback for generic/unhandled errors
    console.error(`[FriendlyError] Unhandled error in context "${context}":`, error);
    return t('error_generic', { context });
}

export const downloadImageFromDataUrl = (dataUrl: string, filename: string) => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const urlToFile = async (
  url: string,
  id: string,
  defaultColor: string | undefined,
  type: 'garment' | 'accessory',
  category: string | undefined,
  iconUrl: string | undefined,
  nameKey: string,
): Promise<{ file: File; item: WardrobeItem }> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} for url: ${url}`);
    }
    const blob = await response.blob();
    const filename = url.substring(url.lastIndexOf('/') + 1) || `${nameKey.replace(/\s+/g, '-')}.${blob.type.split('/')[1] || 'jpg'}`;
    const file = new File([blob], filename, { type: blob.type });

    const item: WardrobeItem = {
      id,
      nameKey,
      url,
      defaultColor,
      type,
      category,
      iconUrl,
      originalFile: file,
    };

    return { file, item };
  } catch (error) {
    console.error(`Error converting URL to File for ${url}:`, error);
    throw new Error(`Failed to load image from URL: ${url}. Please check the console for more details.`);
  }
};

export function hexToHsl(H: string): { h: number; s: number; l: number } {
  let r = 0, g = 0, b = 0;
  if (H.length === 4) {
    r = parseInt("0x" + H[1] + H[1]);
    g = parseInt("0x" + H[2] + H[2]);
    b = parseInt("0x" + H[3] + H[3]);
  } else if (H.length === 7) {
    r = parseInt("0x" + H[1] + H[2]);
    g = parseInt("0x" + H[3] + H[4]);
    b = parseInt("0x" + H[5] + H[6]);
  }
  r /= 255;
  g /= 255;
  b /= 255;
  let cmin = Math.min(r,g,b),
      cmax = Math.max(r,g,b),
      delta = cmax - cmin,
      h = 0,
      s = 0,
      l = 0;

  if (delta === 0) h = 0;
  else if (cmax === r) h = ((g - b) / delta) % 6;
  else if (cmax === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;

  h = Math.round(h * 60);
  if (h < 0) h += 360;

  l = (cmax + cmin) / 2;
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);

  return { h: Math.round(h), s: Math.round(s), l: Math.round(l) };
}

export function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;

  let c = (1 - Math.abs(2 * l - 1)) * s,
      x = c * (1 - Math.abs((h / 60) % 2 - 1)),
      m = l - c/2,
      r = 0,
      g = 0,
      b = 0;

  if (0 <= h && h < 60) {
    r = c; g = x; b = 0;
  } else if (60 <= h && h < 120) {
    r = x; g = c; b = 0;
  } else if (120 <= h && h < 180) {
    r = 0; g = c; b = x;
  } else if (180 <= h && h < 240) {
    r = 0; g = x; b = c;
  } else if (240 <= h && h < 300) {
    r = x; g = 0; b = c;
  } else if (300 <= h && h < 360) {
    r = c; g = 0; b = x;
  }
  let rStr = Math.round((r + m) * 255).toString(16);
  let gStr = Math.round((g + m) * 255).toString(16);
  let bStr = Math.round((b + m) * 255).toString(16);

  if (rStr.length === 1) rStr = "0" + rStr;
  if (gStr.length === 1) gStr = "0" + gStr;
  if (bStr.length === 1) bStr = "0" + bStr;

  return "#" + rStr + gStr + bStr;
}

export function decode(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}

export function encode(bytes: Uint8Array): string {
    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

/**
 * Creates a resized thumbnail from a data URL.
 * Ensures the image fits within maxWidth x maxHeight while maintaining aspect ratio.
 * Returns a JPEG data URL to minimize size.
 */
export async function createThumbnailDataUrl(
  dataUrl: string,
  maxWidth: number,
  maxHeight: number,
  format: 'image/jpeg' | 'image/png' | 'image/webp' = 'image/jpeg',
  quality = 0.7
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      
      let newWidth = img.width;
      let newHeight = img.height;

      // Calculate new dimensions to fit within maxWidth x maxHeight
      // Only downscale, never upscale
      if (newWidth > maxWidth || newHeight > maxHeight) {
          const ratio = Math.min(maxWidth / newWidth, maxHeight / newHeight);
          newWidth = Math.round(newWidth * ratio);
          newHeight = Math.round(newHeight * ratio);
      }

      canvas.width = newWidth;
      canvas.height = newHeight;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return reject(new Error('Could not get canvas context'));
      }
      
      // Fill background for JPEGs (transparency becomes black otherwise)
      if (format === 'image/jpeg') {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, newWidth, newHeight);
      }
      
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      resolve(canvas.toDataURL(format, quality));
    };
    img.onerror = (error) => {
        reject(error);
    };
    img.src = dataUrl;
  });
}

export const sanitizeOutfitsForStorage = async (outfits: SavedOutfit[]): Promise<SavedOutfit[]> => {
    // Helper to compress a potentially large data URL
    const compressDataUrl = async (dataUrl: string, maxDim: number): Promise<string> => {
        if (!dataUrl || !dataUrl.startsWith('data:image')) return dataUrl;
        return await createThumbnailDataUrl(dataUrl, maxDim, maxDim, 'image/jpeg', 0.7);
    };

    const fileToDataURL = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const getSerializableItem = async (item: WardrobeItem) => {
        const { originalFile, ...serializableItem } = item;
        
        // Process URL:
        // 1. If it comes from a file input (blob:), convert to dataURL then compress
        // 2. If it is a dataURL (generated or converted), compress it if large
        // 3. If it is an external URL (http), leave it alone
        
        let urlToProcess = serializableItem.url;

        if (originalFile && urlToProcess.startsWith('blob:')) {
             urlToProcess = await fileToDataURL(originalFile);
        }

        if (urlToProcess.startsWith('data:')) {
             // Compress wardrobe items to max 300px to save space (icons/thumbnails)
             serializableItem.url = await compressDataUrl(urlToProcess, 300);
        } else {
             serializableItem.url = urlToProcess;
        }
        
        return serializableItem;
    };
    
    const sanitizedOutfits = await Promise.all(outfits.map(async (outfit) => {
        // Keep only the current pose output to save space. Others will be regenerated.
        const currentPoseInstruction = outfit.layer.currentPoseInstruction;
        let currentPoseOutputUrl = outfit.layer.poseOutputs[currentPoseInstruction];
        
        // Compress pose output (Main result) -> max 800px (good enough for review)
        if (currentPoseOutputUrl && currentPoseOutputUrl.startsWith('data:')) {
            currentPoseOutputUrl = await compressDataUrl(currentPoseOutputUrl, 800);
        }

        const minimalPoseOutputs = currentPoseOutputUrl ? { [currentPoseInstruction]: currentPoseOutputUrl } : {};

        // Compress preview image -> max 600px
        let previewImageUrl = outfit.previewImageUrl;
        if (previewImageUrl && previewImageUrl.startsWith('data:')) {
            previewImageUrl = await compressDataUrl(previewImageUrl, 600);
        }
        
        // Compress base model image -> max 800px
        let baseModelImageUrl = outfit.baseModelImageUrl;
        if (baseModelImageUrl && baseModelImageUrl.startsWith('data:')) {
             baseModelImageUrl = await compressDataUrl(baseModelImageUrl, 800);
        }

        const cleanLayer: OutfitLayer = {
            ...outfit.layer,
            poseOutputs: minimalPoseOutputs,
            mainGarment: outfit.layer.mainGarment ? await getSerializableItem(outfit.layer.mainGarment) : null,
            accessories: await Promise.all(outfit.layer.accessories.map(getSerializableItem)),
        };

        return {
            ...outfit,
            previewImageUrl,
            baseModelImageUrl,
            layer: cleanLayer,
        };
    }));

    return sanitizedOutfits;
};
